import { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';

/**
 * Saves the current page as a draft before leaving. Resolves true when saved (navigation may
 * continue) and false when the save did not happen (the user stays on the page).
 */
export type SaveDraftBeforeLeaveHandler = (destination: string) => Promise<boolean>;

interface UnsavedChangesContextType {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (value: boolean) => void;
  pendingNavigation: string | null;
  setPendingNavigation: (path: string | null) => void;
  confirmNavigation: () => void;
  cancelNavigation: () => void;
  unsavedMessage: string | null;
  setUnsavedMessage: (message: string | null) => void;
  /** Pages that support drafts register a handler so the leave prompt can offer "Save Draft". */
  setSaveDraftHandler: (handler: SaveDraftBeforeLeaveHandler | null) => void;
  getSaveDraftHandler: () => SaveDraftBeforeLeaveHandler | null;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextType | null>(null);

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [unsavedMessage, setUnsavedMessage] = useState<string | null>(null);
  const saveDraftHandlerRef = useRef<SaveDraftBeforeLeaveHandler | null>(null);

  const setSaveDraftHandler = useCallback((handler: SaveDraftBeforeLeaveHandler | null) => {
    saveDraftHandlerRef.current = handler;
  }, []);

  const getSaveDraftHandler = useCallback(() => saveDraftHandlerRef.current, []);

  const confirmNavigation = useCallback(() => {
    setHasUnsavedChanges(false);
    setPendingNavigation(null);
  }, []);

  const cancelNavigation = useCallback(() => {
    setPendingNavigation(null);
  }, []);

  return (
    <UnsavedChangesContext.Provider
      value={{
        hasUnsavedChanges,
        setHasUnsavedChanges,
        pendingNavigation,
        setPendingNavigation,
        confirmNavigation,
        cancelNavigation,
        unsavedMessage,
        setUnsavedMessage,
        setSaveDraftHandler,
        getSaveDraftHandler,
      }}
    >
      {children}
    </UnsavedChangesContext.Provider>
  );
}

export function useUnsavedChangesContext() {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error('useUnsavedChangesContext must be used within UnsavedChangesProvider');
  }
  return context;
}
